import { z } from "zod";

export const loadSchema = z.object({
	poNumber: z.number().nullable(),
	// Accept either pickupDate or loadDate. TraxAR (internal) returns `loadDate`;
	// LTL Quoting (external) returns `pickupDate`. Coalesced in useLoadsData.
	pickupDate: z.string().nullable().optional(),
	loadDate: z.string().nullable().optional(),
	customerName: z.string().nullable(),
	customerId: z.number().nullable(),
	customerPO: z.string().nullable(),
	// TraxAR (internal) returns `carrierName`; kept here so Zod does not strip it
	// before the useLoadsData transform maps it to `ltlCarrierName`.
	carrierName: z.string().nullable().optional(),
	deliveryDate: z.string().nullable(),
	isDropsDelivered: z.boolean().nullable(),
	isLoaded: z.boolean().nullable(),
	lane: z.string().nullable(),
	trailerType: z.string().nullable(),
	temperature: z.string().nullable(),
	weight: z.number().nullable(),
	pickupPOList: z.string().nullable(),
	firstPickName: z.string().nullable(),
	firstPickCity: z.string().nullable(),
	firstPickState: z.string().nullable(),
	firstPickZIP: z.string().nullable(),
	deliveryPOList: z.string().nullable(),
	lastDropName: z.string().nullable(),
	lastDropCity: z.string().nullable(),
	lastDropState: z.string().nullable(),
	lastDropZIP: z.string().nullable(),
	lastDropDeliveryPO: z.string().nullable(),
	lastDropDeliveryDate: z.string().nullable(),
	// Most recent known location for the shipment. Declared `.nullable().optional()`
	// so rows from an API build that does not yet return them validate cleanly.
	lastLocationCity: z.string().nullable().optional(),
	lastLocationState: z.string().nullable().optional(),
	loadStatus: z.string(),
	proNumber: z.string().nullable(),
	shipmentTypeId: z.number().nullable(),
	quoteId: z.string().nullable(),
	mgLoadId: z.string().nullable(),
	custRefNum: z.string().nullable(),
	soNumber: z.string().nullable(),
	xaShipmentId: z.string().nullable(),
	minFreightDBChgsId: z.number().nullable(),
	minFreightDBChangeUTCDT: z.string().nullable(),
	maxFreightDBChgsId: z.number().nullable(),
	maxFreightDBChangeUTCDT: z.string().nullable(),
	minMoveDBChangeUTCDT: z.string().nullable(),
	maxMoveDBChangeUTCDT: z.string().nullable(),
	brokerId: z.number().nullable(),
	loadStatusInfo: z.number().nullable(),
	rowLastUpdatedUtc: z.string(),
	isVoided: z.boolean().nullable(),
	isDispatched: z.boolean().nullable(),
	isNeedsAttention: z.boolean().nullable(),
	formattedPickupAddress: z.string().optional(),
	formattedDeliveryAddress: z.string().optional(),
	// Derived in the useLoadsData transform as "City, ST" from
	// lastLocationCity / lastLocationState (blank when both are absent).
	formattedLastLocation: z.string().optional(),
	// Date instances derived from `pickupDate` / `deliveryDate` so the grid can
	// sort and filter (`filter: "date"`) on real Date values rather than
	// pre-formatted strings.
	loadDateValue: z.date().nullable().optional(),
	deliveryDateValue: z.date().nullable().optional(),
	formattedStatus: z.string().optional(),
	isImmediateAction: z.boolean().optional(),
	isNeedsAcknowledged: z.boolean().optional(),
	rateType: z.string().nullable().optional(),
	rateTypeId: z.number().nullable().optional(),
	// External: populated by the LTL Quoting API only for rateType === "LTL" (backend-filtered).
	// Internal: derived from `carrierName` (TraxAR) in the useLoadsData transform.
	ltlCarrierName: z.string().nullable().optional(),
	primaryBrokerName: z.string().nullable().optional(),
	ltlArName: z.string().nullable().optional(),
	ediStatus: z.string().nullable().optional(),
	omassStatus: z.string().nullable().optional(),
	ftlGsm: z.string().nullable().optional(),
	serviceLevel: z.string().nullable().optional(),
});

// Array schema for multiple loads
export const loadsArraySchema = loadSchema.array();

// Schema for shipment statistics/summary data
const countWithExceptions = z.object({
	count: z.number(),
	exceptionsCount: z.number(),
});

export const shipmentStatsSchema = z.object({
	todaysShipmentsTotal: z.number(),
	todaysShipmentsNotDispatched: countWithExceptions,
	todaysShipmentsDispatched: countWithExceptions,
	todaysShipmentsInTransit: countWithExceptions,
	todaysShipmentsDelivered: countWithExceptions,
	futureShipmentsTotal: z.number(),
	futureShipmentsNotDispatched: z.number(),
	futureShipmentsDispatched: z.number(),
	totalActiveShipments: z.number(),
	shipmentsNeedingAttention: z.number(),
	totalWeight: z.number().optional(),
	uniqueCarriersCount: z.number(),
	isDefault: z.boolean().optional(),
});

export const shipmentStatusSchema = z.object({
	value: z.number(),
	name: z.string(),
	displayName: z.string(),
});

// Schema for a customer/broker reference object
export const customerByArSchema = z.object({
	id: z.number(),
	name: z.string(),
	hasSignedLTLAddendum: z.boolean(),
	status: z.object({
		id: z.number(),
		name: z.string().nullable(),
	}),
	primaryBroker: z.any().nullable(),
	ltlBroker: z.any().nullable(),
});

export const shipmentStatusArraySchema = shipmentStatusSchema.array();

export type Load = z.infer<typeof loadSchema>;
export type LoadsArray = z.infer<typeof loadsArraySchema>;
export type ShipmentStats = z.infer<typeof shipmentStatsSchema>;
export type ShipmentStatus = z.infer<typeof shipmentStatusSchema>;
export type ShipmentStatusArray = z.infer<typeof shipmentStatusArraySchema>;

export type CustomerByAr = z.infer<typeof customerByArSchema>;
